/**
 * Handlers for notifications and error messages.
 *
 * @packageDocumentation
 */
import { createStore } from './Store';
import React, { useMemo } from 'react';
import { produce } from 'immer';
import type {
  ClassNameProp,
  ClosableComponentProps,
  LanguageProperty,
  ValidIconName,
  VoidCallback
} from '../components';
import { Button, Icon, Modal, Tool } from '../components';
import type { UUID } from '../util';
import { uuidv4 } from '../util';
import { intl } from './format/Language';
import type { ImmerHook } from 'use-immer';
import { useImmer } from 'use-immer';
import type { CodedError } from './Error';
import ReactDOM from 'react-dom';

/**
 * The value type of the notification store.
 */
export type NotificationState = {
  /** The element to render */
  element: React.JSX.Element;
  /** Key for deletion */
  key: UUID;
  /** to determine if it's a modal or a toast. */
  notificationType: 'modal' | 'toast';
  /** render to a specific div */
  renderTo?: string;
};

/**
 * Toast default values type
 */
type ToastDefaultType = {
  /** className */
  className: string;
  /** title */
  title: LanguageProperty;
};
/**
 * All available notification types.
 *
 * Don't forget to update the css classes (`message-${type.toLowerCase}`) in `Notifications.scss` when modifying this enum!
 */
export enum NotificationType {
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS'
}

const icons: Record<NotificationType, ValidIconName | undefined> = {
  [NotificationType.INFO]: undefined,
  [NotificationType.WARN]: 'materialicon-error',
  [NotificationType.ERROR]: 'shui_error',
  [NotificationType.SUCCESS]: 'materialicon-check_circle'
};

/**
 * Maps each notification type to the appropriate ARIA live-region role.
 * ERROR and WARN are urgent and interrupt the user (assertive); INFO and SUCCESS
 * are polite and wait for the user to finish their current task.
 */
const toastLiveRegionRole: Record<NotificationType, 'alert' | 'status'> = {
  [NotificationType.ERROR]: 'alert',
  [NotificationType.WARN]: 'alert',
  [NotificationType.INFO]: 'status',
  [NotificationType.SUCCESS]: 'status'
};

/**
 * Default toast values by notification types
 */
const toastsDefaults: Record<NotificationType, ToastDefaultType> = {
  [NotificationType.INFO]: {
    className: 'toast-info',
    title: 'view.clearingticket.severity.INFO'
  },
  [NotificationType.WARN]: {
    className: 'toast-warning',
    title: 'common.warning'
  },
  [NotificationType.ERROR]: {
    className: 'toast-error',
    title: 'common.error'
  },
  [NotificationType.SUCCESS]: {
    className: 'toast-success',
    title: 'common.success'
  }
};

/**
 * Configurable style of Toast messages.
 */
export interface ToastNotificationStyle {
  /** How long the toast message is visible, in milliseconds. */
  timeout: number;
  /** If a button for closing the message should be present. */
  closeButton: boolean;
}

/**
 * Different positions that toasts can be showed in.
 * This is only for the entire Toasts container and not for individual toasts.
 */
export enum ToastsPosition {
  BOTTOM_CENTER = 'bottom_center',
  BOTTOM_LEFT = 'bottom_left',
  BOTTOM_RIGHT = 'bottom_right',
  TOP_CENTER = 'top_center',
  TOP_LEFT = 'top_left',
  TOP_RIGHT = 'top_right'
}
/**
 * Store to hold all active notifications.
 *
 * This allows them to be rendered and cleaned up.
 */
export interface NotificationStore {
  /** how to render each kind of notification */
  config: {
    notificationType: Record<NotificationType, 'modal' | 'toast'>;
    toastStyle: ToastNotificationStyle;
  };
  /** all message boxes to currently render */
  notifications: Array<NotificationState>;
  /** add a new notification */
  addNotification(n: NotificationState): boolean;
  /** remove a notification with the given key */
  removeNotification(key: UUID): boolean;
  /** update config */
  updateConfig: (config: NotificationStore['config'] | undefined) => void;
  /**
   * Notification that will be read by screen-readers.
   * This is a string since you can only have one notification read at a time,
   * and we can't control how long it takes for the reader to read the text.
   */
  screenReaderNotification: string;
  /** Setter for screenReaderNotification state. */
  setScreenReaderNotification: (message: string) => void;
}

/**
 * Store that maintains active notifications.
 *
 * This should not be used directly.
 *
 */
export const [
  useNotificationStore,
  NotificationStoreApi
] = createStore<NotificationStore>('notifications.store', (set, get) => ({
  notifications: [],
  screenReaderNotification: '',
  config: {
    notificationType: {
      [NotificationType.ERROR]: 'modal',
      [NotificationType.WARN]: 'modal',
      [NotificationType.INFO]: 'toast',
      [NotificationType.SUCCESS]: 'toast'
    },
    toastStyle: {
      timeout: 20000,
      closeButton: true
    }
  },

  addNotification: (n): boolean => {
    set(
      produce((state: NotificationStore) => {
        if (state.notifications.find(n2 => n2.key === n.key)) {
          return false;
        }

        state.notifications.push(n);
      }),
      'addNotification'
    );
    return true;
  },

  removeNotification: (key): boolean => {
    const notifications = get().notifications;
    const filtered = notifications.filter(n => n.key !== key);
    set({ notifications: filtered }, 'removeNotification');

    return filtered.length < notifications.length;
  },

  updateConfig: (config): void => {
    if (config) set({ config: config }, 'updateConfig');
  },

  setScreenReaderNotification: message => {
    set({ screenReaderNotification: message }, 'setScreenReaderNotification');
  }
}));

/**
 * Parameters for notification creation.
 */
export interface NotificationParams {
  /** The title of the toast or modal */
  title: string;
  /** The body of the toast or modal. */
  message?: string;
  /** The kind of notification */
  type: NotificationType;
  /** Optional callback to be triggered when the notification is closed */
  onClose?: VoidCallback;
}

/**
 * Options for creating custom message boxes.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface CustomNotificationOptions<State>
  extends Omit<NotificationParams, 'message'>,
    ClassNameProp {
  /**
   * Whether to close the message box when the overlay is clicked.
   *
   * This defaults to true in notifyCustom and for non-error notifications.
   */
  closeOnOverlayClicked?: boolean;
  /**
   * Optional icon override.
   *
   * If not set, the icon will be chosen according to the notification type.
   */
  icon?: ValidIconName;
  /**
   * Action buttons to put in the bottom right.
   *
   * The primary action should come first because it will be aligned to the right (flex-direction: row-reversed).
   */
  actions?: React.JSX.Element;
  /**
   * The main content of the modal.
   */
  content: React.JSX.Element;
}

/**
 * Create a custom message box notification.
 *
 * This function will always show a message box, regardless of NotificationsHook configuration.
 * The `closingCallback` argument to the creator function automatically wraps your callback and closes the message box before calling it.
 * i.e. use `closingCallback(...)` to get a function that will close the message box.
 *
 * If you pass a type param, the creator will also have access to (state, setState) for an object of the given type.
 * That allows you to e.g. manage a form in the confirmation dialog which only allows confirming when a field has been filled out!
 * See the example story for usage.
 *
 * @param creator function that receives a callback factory and returns an object of options for the message box
 * @param initialState optional initial state to set
 *
 *
 * @example
 * notifyCustom(closingCallback => {
    const cancel = closingCallback(() => void console.log('cancelled'));
    const accept = closingCallback(() => void console.log('accepted'));
    return {
      title: 'Title',
      onClose: cancel,
      type: NotificationType.INFO,
      content: <div>content</div>,
      actions: (
        <>
          <Button variant="gray" onClick={cancel} text="Cancel" />
          <Button variant="primary" onClick={accept} text="Accept" />
        </>
      )
    };
  });
 */
export function notifyCustom<State = void>(
  creator: (
    closingCallback: (arg: VoidCallback | undefined) => VoidCallback,
    state: Partial<State>,
    setState: ImmerHook<Partial<State>>[1]
  ) => CustomNotificationOptions<Partial<State>>,
  initialState?: State,
  renderTo?: string
): void {
  const key = uuidv4();

  const closingCallback = (
    cb: VoidCallback | undefined
  ): VoidCallback => (): void => {
    console.debug('cleaning up notification', key);
    NotificationStoreApi.getState().removeNotification(key);
    cb?.();
  };

  const MessageBox = (): React.JSX.Element => {
    const [state, setState] = useImmer(initialState ?? ({} as Partial<State>));

    const {
      closeOnOverlayClicked,
      content,
      onClose,
      title,
      actions,
      type,
      icon,
      className
    } = creator(closingCallback, state, setState);

    let closable: ClosableComponentProps['closable'];

    if (NotificationType.ERROR === type) {
      closable = false;
    } else {
      closable = { onClose: onClose ?? closingCallback(undefined) };
    }

    const closeOnOverlay = closeOnOverlayClicked ?? true;

    //exception for the error dialog, the alert role should be only on the error code message not the whole content element.
    const modalContent = (): React.JSX.Element => {
      if (type !== NotificationType.ERROR) {
        return <span role="alert">{content}</span>;
      } else {
        return <div>{content}</div>;
      }
    };

    return (
      <Modal
        onOverlayClicked={
          closeOnOverlay ? onClose ?? closingCallback(undefined) : undefined
        }
        overlayClassName={`notification overlay-${type.toLowerCase()}`}
        title={title}
        icon={icon ?? icons[type]}
        className={`messagebox message-${type.toLowerCase()} ${
          className ?? ''
        }`.trim()}
        closable={closable}
        actions={actions}
      >
        {modalContent()}
      </Modal>
    );
  };

  NotificationStoreApi.getState().addNotification({
    element: <MessageBox key={key} />,
    key: key,
    notificationType: 'modal',
    renderTo
  });
}

/**
 * Show a toast notification.
 *
 * @param message The message.
 * @param type The kind of notification.
 * @param onClose optional callback for toast expiry
 */
export function toast(
  message: string,
  type: NotificationType,
  onClose: VoidCallback | undefined
): void {
  const { className, title } = toastsDefaults[type];
  const iconName = icons[type];

  const {
    timeout,
    closeButton
  }: ToastNotificationStyle = NotificationStoreApi.getState().config.toastStyle;

  const removeToast = () => {
    const removed = NotificationStoreApi.getState().removeNotification(key);
    // onClose only gets called if the notification was actually removed
    // to prevent multiple calls of onClose due to the timeout and closeButton both existing
    if (removed) onClose?.();
  };

  const key = uuidv4();
  NotificationStoreApi.getState().addNotification({
    element: (
      <div
        key={key}
        className="ehs-toast ehs-vbox"
        role={toastLiveRegionRole[type]}
      >
        <div className="toast-title">
          {iconName && (
            <Icon
              iconName={iconName}
              className={className}
              aria-label={undefined}
              fontBoostWidthMultiplier={1}
              fontBoostHeightMultiplier={1}
            />
          )}
          <h2 className={`title-text ${!iconName ? 'no-icon' : ''}`}>
            {intl(title)}
          </h2>
          {closeButton && (
            <Tool
              onClick={removeToast}
              icon="materialicon-close"
              text={intl('view.button.close')}
              className="close-toast-tool"
              fontBoostWidthMultiplier={1}
              fontBoostHeightMultiplier={1}
            />
          )}
        </div>
        <span>{message}</span>
      </div>
    ),
    key: key,
    notificationType: 'toast'
  });

  setTimeout(removeToast, timeout);
}

/**
 * Log a pretty message to the console.
 *
 * TODO: the logs could be made prettier.
 */
export function prettyLog({
  title,
  message,
  type,
  code,
  uniqueCorrelationId
}: Pick<NotificationParams, 'message' | 'type' | 'title'> &
  Partial<Pick<CodedError, 'code' | 'uniqueCorrelationId'>>): void {
  const newTitle = title + ` (${code}) - ${uniqueCorrelationId}`;
  switch (type) {
    case NotificationType.INFO:
      console.info('INFO:', newTitle, message);
      break;
    case NotificationType.WARN:
      console.warn('WARN:', newTitle, message);
      break;
    case NotificationType.ERROR:
      console.error('ERROR:', newTitle, message);
      break;
    case NotificationType.SUCCESS:
      console.info('SUCCESS:', newTitle, message);
      break;
  }
}

/**
 * Trigger a new notification.
 *
 * Depending on the configuration of the NotificationsHook and the given {@link NotificationType},
 * this may be a toast or a modal.
 *
 * @param options The {@link NotificationParams}
 *
 */
export function notify({
  message,
  onClose,
  title,
  type
}: NotificationParams): void {
  prettyLog({ title, message, type });

  const isToast =
    NotificationStoreApi.getState().config.notificationType[type] === 'toast';
  if (isToast) {
    toast(message ? `${title}: ${message}` : title, type, onClose);
    return;
  }

  notifyCustom(closingCallback => {
    const close = closingCallback(() => onClose?.());
    return {
      type,
      title: message ? title : intl(toastsDefaults[type].title),
      closeOnOverlayClicked:
        type !== NotificationType.ERROR && type !== NotificationType.WARN,
      onClose: close,
      content: <div>{message ?? title}</div>,
      actions: (
        <Button
          variant="primary"
          onClick={close}
          text={intl('view.button.ok')}
        />
      )
    };
  });
}

/**
 * A component that renders all message boxes.
 *
 * Should only be used in the storybook or in the NotificationsHook!
 *
 * @param className Optional scope class applied to an ancestor of the message boxes.
 */
export function NotificationRenderer({
  className
}: { className?: string } = {}): React.JSX.Element {
  const notificationData = useNotificationStore(state =>
    state.notifications.filter(ns => ns.notificationType === 'modal')
  );

  const [customRendered, normalRendered] = useMemo(() => {
    const customRendered = [];
    const normalRendered = [];
    for (const ns of notificationData) {
      if (ns.renderTo) {
        customRendered.push(<CustomRendered key={ns.key} element={ns} />);
      } else {
        normalRendered.push(ns.element);
      }
    }
    return [customRendered, normalRendered];
  }, [notificationData]);
  return (
    <>
      {ReactDOM.createPortal(
        <div className={className}>{normalRendered}</div>,
        document.body
      )}
      {customRendered}
    </>
  );
}

type CustomRenderedProps = {
  element: NotificationState;
};
/**
 * If set, render Modal into some div with specified ID.
 *
 * @param element Any Modal Notification that should be rendered to the DOM
 */
function CustomRendered({ element }: CustomRenderedProps): React.JSX.Element {
  const domElement = document.getElementById(element.renderTo ?? '');
  if (!domElement) {
    console.error(
      'DOM Element ${element.renderTo} does not exist. Custom rendered Notification cannot be rendered!'
    );
    return <></>;
  }
  return ReactDOM.createPortal(<>{element.element}</>, domElement);
}

/**
 * Props for {@link ToastRenderer}.
 */
export type ToastRendererProps = {
  /** position of the toast messages. */
  position: ToastsPosition;
  /** optional extra CSS class applied to the toasts container, e.g. for portal-specific styling. */
  className?: string;
};

/**
 * A component that renders all toast messages.
 *
 * Should only be used in the storybook or in the NotificationsHook!
 *
 * @param props {@link ToastRendererProps}
 * @constructor
 */
export function ToastRenderer({
  position = ToastsPosition.BOTTOM_LEFT,
  className
}: ToastRendererProps): React.JSX.Element {
  const notifications = useNotificationStore(state =>
    state.notifications.filter(n => n.notificationType === 'toast')
  );
  return ReactDOM.createPortal(
    <div
      className={`ehs-toasts-container position-${position}${
        className ? ` ${className}` : ''
      }`}
    >
      {notifications.map(n => n.element)}
    </div>,
    document.body
  );
}

/**
 * Parameters for creation of a confirm notification.
 */
export interface ConfirmNotificationParams {
  /**
   * Optional custom dialog title.
   */
  title?: string;
  /**
   * The confirmation message.
   */
  message: string;
  /**
   * Callback to execute when the action is confirmed.
   */
  onConfirm: VoidCallback;
  /**
   * Optional callback to execute when the action is rejected.
   */
  onReject?: VoidCallback;

  /**
   * Optional callback to execute when the dialog is closed without selecting actions.
   * If not provided onReject callback will be called instead.
   */
  onClose?: VoidCallback;
}

/**
 * Trigger a new confirmation notification.
 * This method makes use of {@link notifyCustom} to create a typical
 * confirmation dialog to ask the user to confirm some kind of action.
 *
 * @param props {@link ConfirmNotificationParams}
 */
export function notifyConfirm({
  message,
  onConfirm,
  onReject,
  title = intl('common.confirmation'),
  onClose
}: ConfirmNotificationParams): void {
  notifyCustom(closingCallback => {
    const confirmCb = closingCallback(() => onConfirm());
    const rejectCb = closingCallback(() => onReject?.());
    const closeCb = closingCallback(() => onClose?.());
    return {
      title: title,
      onClose: onClose ? closeCb : rejectCb,
      type: NotificationType.WARN,
      closeOnOverlayClicked: false,
      content: <div>{message}</div>,
      actions: (
        <>
          <Button
            variant="primary"
            onClick={confirmCb}
            text={intl('common.yes')}
          />
          <Button variant="gray" onClick={rejectCb} text={intl('common.no')} />
        </>
      )
    };
  });
}

/**
 * Screen-reader only element to notify screen-reader users of specific events happening on the page (e.g. when a document was opened).
 */
export function ScreenReaderAlertRenderer(): React.JSX.Element {
  const message = useNotificationStore(state => state.screenReaderNotification);
  return (
    <div
      className="sr-only"
      aria-live="polite"
      aria-relevant="additions"
      aria-atomic={true}
    >
      {message}
    </div>
  );
}

/**
 * Trigger a new notification that will be read by screen-readers.
 *
 * @param message the message to read to the user
 */
export function notifyScreenReader(message: string): void {
  // First, we clear the field, and then on the next render we set the message.
  // This is necessary because otherwise, having the same message read multiple times in succession would be impossible.
  NotificationStoreApi.getState().setScreenReaderNotification('');
  setTimeout(
    () => NotificationStoreApi.getState().setScreenReaderNotification(message),
    0
  );
}
